import { useMutation } from '@apollo/client';
import type { InsertAttestationConfigMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertAttestationConfigDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import {
  type InsertAttestationConfigInput,
  useInsertAttestationConfigTRPC,
} from './useInsertAttestationConfigTRPC';

/**
 * Flag-gated wrapper for the `insertAttestationConfig` mutation. Defaults to
 * the legacy Apollo mutation and delegates to the tRPC variant when the `trpc`
 * feature flag is enabled. The public input shape matches the legacy
 * `InsertAttestationConfigInput` so consumers only swap the import.
 */
export const useInsertAttestationConfig = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [insertGraphQL, graphqlState] = useMutation(
    InsertAttestationConfigDocument
  );

  const trpcMutation = useInsertAttestationConfigTRPC();

  const insertAttestationConfig = async (
    input: InsertAttestationConfigInput
  ): Promise<InsertAttestationConfigMutation> => {
    if (trpcEnabled) {
      return trpcMutation.insertAttestationConfig(input);
    }

    const result = await insertGraphQL({
      variables: {
        object: {
          ParentId: input.ParentId,
          RequireGlobalAttestation: input.RequireGlobalAttestation,
          AttestationGroupIds: input.AttestationGroupIds,
          AttestationPromptText: input.AttestationPromptText,
          AttestationTimeLimit: input.AttestationTimeLimit,
        },
      },
    });
    if (!result.data) {
      throw new Error('Failed to save attestation config');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      insertAttestationConfig,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    insertAttestationConfig,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
